import { Injectable, Signal, WritableSignal, computed, signal } from "@angular/core";
import { E_Patient_Actions_Type } from "@backend/graph/patient_actions/patient-action-base";
import { logger } from "@dentally/ui/logger";
import { FORMATS } from "@shared/constants";
import { SubSink } from "subsink";
import { GAService } from "./ga.service";
import { GRAPHQL_OPERATION_NAMES, HttpService } from "./http.service";
import { NavigationService } from "./navigation.service";
import { NotificationService, NotificationTypes } from "./notification.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { E_ActionType } from "@backend/graph/patients/patient-base";
import { AnalyticsService, METRIC } from "./analytics.service";
import { PatientNhsPrBase } from "@backend/graph/patient_nhs_pr/patient-nhs-pr-base";
import { ExemptionOption } from "@dentally/business-logic/nhs-pr/exemptions/exemption-options";
import { CreatePatientNhsPrFormBase } from "@backend/graph/patient_nhs_pr/create-patient-nhs-pr/create-patient-nhs-pr-base";
import { PatientActionsEntry } from "@apps/patient-core/src/app/data_model/patient-actions";
import { BaseAuthService } from "./abstracts/auth.service";
import { PatientActionsCacheService } from "./patient-actions-cache.service";

dayjs.extend(advancedFormat);

export interface I_NHS_PR {
  status: "LOADING" | "LOADED" | "SAVED";
  data?: PatientNhsPrBase;
}

const GRAPHQL_QUERY = (languageCode: string) => {
  const args = `${languageCode ? `language_code: "${languageCode}"` : ""}`;

  return `{
  patient {
    date_of_birth
    nhs_pr(${args}) {
      meta_data {
        treatment_plans {
          total_cost
        }
        appointments {
          appointment_sid
          practitioner_name
        }
      }
      appointments {
        id
        practitioner_id
      }
      form_type
      form_required
      legal_text {
        FP_17_BASIC
        FP_17_EXEMPTION_DEFAULT
        FP_17_PATIENT_RESPONSIBILITY
        GP_17_EXEMPTION_DEFAULT
      }
      patient_exemption {
        exemption_code
        updated_at
        description
      }
      exemption_options {
        exemption_code
        description
        intro
        group
        details {
          title
          intro
          fields {
            field
            label
            type
            placeholder
            more_info
            link {
              url
              text
              target
            }
            validation {
              nhs_number
              pattern
              date {
                type
                value
              }
              groupId
              required
              number {
                min
                max
              }
            }
          }
        }
        legal_text
      }
      display_groups {
        id
        intro
      }
      language_codes
      language_code
      updated_at
    }
  }
}`;
};

export interface I_LanguageSwitch {
  buttonText: string;
  show: boolean;
  language_code: string;
  switch_to_language_code: string;
  switch_to_buttonText: string;
}

@Injectable({
  providedIn: "root",
})
export class NhsPrService {
  private _subs = new SubSink();
  public new_pr_form: CreatePatientNhsPrFormBase = {};
  public selected_exemption: ExemptionOption = {};
  private _prState: WritableSignal<I_NHS_PR | null> = signal(null);
  public prState = this._prState.asReadonly();
  public formUrl = signal<string | null>(null);
  private _isFetching = false;

  constructor(
    private _httpService: HttpService,
    private _navigationService: NavigationService,
    private _notificationService: NotificationService,
    private _gaService: GAService,
    private _patientsService: BasePatientsService,
    public _authService: BaseAuthService,
    private _analyticsService: AnalyticsService,
    private _patientActionsCacheService: PatientActionsCacheService
  ) {}

  public get pr_data(): PatientNhsPrBase | undefined {
    return this.prState()?.data;
  }

  public get loading(): Signal<boolean> {
    return computed(() => {
      return this.prState()?.status === "LOADING";
    });
  }

  public get loaded(): Signal<boolean> {
    return computed(() => {
      return this.prState()?.status === "LOADED";
    });
  }

  public get saved(): Signal<boolean> {
    return computed(() => {
      return this.prState()?.status === "SAVED";
    });
  }

  public readonly legalText = computed(() => {
    const data = this.prState()?.data;
    if (!data?.legal_text) return [""];

    const legalText: Array<string> = [];
    const allLegal = data.legal_text;

    if (this.selected_exemption?.legal_text?.length) {
      this.selected_exemption.legal_text.forEach((i) => {
        legalText.push(allLegal[i] || "");
      });
    } else {
      Object.values(allLegal).forEach((text: string) => {
        if (text) legalText.push(text);
      });
    }

    return legalText;
  });

  public langSwitch = computed<I_LanguageSwitch>(() => {
    const pr = this.prState()?.data;

    const defaultValue: I_LanguageSwitch = {
      buttonText: "Cymraeg",
      show: false,
      language_code: "en",
      switch_to_language_code: "cy",
      switch_to_buttonText: "English",
    };

    if (!pr || !pr.language_codes || pr.language_codes.length !== 2) return { ...defaultValue, show: false };

    const langCode = pr.language_code;

    switch (langCode) {
      case "en":
        if (pr.language_codes.includes("cy")) {
          return {
            show: true,
            buttonText: "Cymraeg",
            switch_to_buttonText: "English",
            switch_to_language_code: "cy",
            language_code: "en",
          };
        }
        break;

      case "cy":
        if (pr.language_codes.includes("en")) {
          return {
            show: true,
            buttonText: "English",
            switch_to_buttonText: "Cymraeg",
            switch_to_language_code: "en",
            language_code: "cy",
          };
        }
        break;
    }

    return { ...defaultValue, show: false };
  });

  getPrData(force?: boolean, language_code = "en"): void {
    if (this._isFetching) {
      const interval = setInterval(() => {
        if (!this._isFetching) {
          clearInterval(interval);
          return;
        }
      }, 300);
      return;
    }

    this._prState.set({ status: "LOADING", data: this.pr_data });

    if (this.pr_data && !force) {
      this._prState.set({
        status: "LOADED",
        data: this.pr_data,
      });
    } else {
      this._isFetching = true;
      this._subs.sink = this._httpService.query(GRAPHQL_OPERATION_NAMES.NHS_PR, GRAPHQL_QUERY(language_code)).subscribe(
        (response) => {
          const pr_data: PatientNhsPrBase = {
            age: response.data.patient.age,
            date_of_birth: response.data.patient.date_of_birth,
            ...response.data.patient.nhs_pr,
          };

          if (pr_data.patient_exemption?.exemption_code) this.selected_exemption = pr_data.patient_exemption;
          if (!pr_data.patient_exemption?.description) delete pr_data.patient_exemption;

          this._prState.set({
            status: "LOADED",
            data: pr_data,
          });

          this._isFetching = false;
        },
        () => {
          this._isFetching = false;
          this.saveError();
        }
      );
    }
  }

  public get isPatientVerificationRequired(): boolean {
    if (!this._authService.isPatientUnauthenticated()) return false;

    return !this._patientsService.patientInfo?.date_of_birth || !this._patientsService.patientInfo?.last_name;
  }

  public get patientResponsibilityLegalText(): string {
    const legalText = this.pr_data?.legal_text;

    if (legalText && "FP_17_PATIENT_RESPONSIBILITY" in legalText) {
      return legalText.FP_17_PATIENT_RESPONSIBILITY;
    }

    return "";
  }

  changeLanguage() {
    const current = this.langSwitch();
    this.getPrData(true, current.switch_to_language_code);
  }

  saveSuccess() {
    this._gaService.action("nhs_pr_exemption_sign_success");
    this._navigationService.navigate("/my-dental");
  }

  saveError() {
    this._navigationService.navigate("/my-dental/nhs-pr/terms");
    this._notificationService.open({
      type: NotificationTypes.ERROR,
      title: "There was an unexpected error, please try again",
    });
    this._gaService.action("nhs_pr_exemption_sign_error");
    try {
      logger.error("nhs_pr_exemption_sign_error");
    } catch (err) {}
  }

  private _isActionRequired(action: PatientActionsEntry): boolean {
    return action.type === E_Patient_Actions_Type.NHS_PR && action.required;
  }

  public get action(): PatientActionsEntry | undefined {
    return this._patientActionsCacheService.patientActions?.find((i) => this._isActionRequired(i));
  }

  private _updateAction(): void {
    const { patientActions } = this._patientActionsCacheService;
    const requiredAction = patientActions?.find((action) => this._isActionRequired(action));

    if (!requiredAction || !this.formUrl()) return;

    requiredAction.form_url = this.formUrl();
    this._patientActionsCacheService.cachePatientActions(patientActions, E_Patient_Actions_Type.NHS_PR);
  }

  private _trackSigned() {
    const referrer = this._authService.referrerAsJson;
    const { site_id } = this._authService.getJWT();
    if (this.action) {
      const { pms_action_type } = this.action;

      this._analyticsService.track(new METRIC.ActionSigned_NhsPr(site_id, referrer, pms_action_type));
    }
  }

  public signForm(): void {
    this._trackSigned();
    this._prState.set({
      data: this.pr_data,
      status: "LOADING",
    });

    const mutation = this._getGraphQlMutation();
    this._httpService.mutation("createPatientNhsPrExemption", mutation).subscribe((response) => {
      try {
        if (!response.errors?.length) {
          this._prState.set({
            status: "SAVED",
          });
          this.new_pr_form = {};

          // form signing is handled on a job queue in the PMS, so if we request pr data again here, it may show as
          // still being required for a short period. So instead, just set the form required to false so that we don't get weird behaviour
          const current = this.prState();
          const currentData = current?.data;

          if (current && currentData) {
            this._prState.set({
              status: current.status,
              data: {
                ...currentData,
                form_required: false,
              },
            });
          }

          this.formUrl.set(response.data.createPatientNhsPrExemption.url);
          this._updateAction();

          this.saveSuccess();

          if (this.new_pr_form.date_of_birth && this._patientsService.patientInfo && !this._patientsService.patientInfo?.date_of_birth) {
            this._patientsService.patientInfo.date_of_birth = this.new_pr_form.date_of_birth;
          }
        } else {
          throw response.errors[0].message;
        }
      } catch (err) {
        this._handleCreateError(err);
      }
    });
  }

  public get isScotland(): boolean {
    return !!this._patientsService.isNHSScotland;
  }

  private _handleCreateError(error?: string) {
    if (error === "NOT_REQUIRED__ALREADY_SIGNED") {
      this._gaService.action("nhs_pr_NOT_REQUIRED__ALREADY_SIGNED");
      this._backToForm();
    } else {
      this.getPrData(true);
      this.saveError();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _formatFields(fields: string[], data: Record<string, any>): string {
    return fields
      .map((key) => data[key] && `${key}: "${this._formatValue(key, data[key])}"`)
      .filter(Boolean)
      .join(",");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _formatValue(key: string, value: any): string {
    return key === "date_of_birth" ? dayjs(value, FORMATS.LOCALE_DATE).format("YYYY-MM-DD") : value;
  }

  private get _benefitRecipientFields(): Array<string> {
    return ["ni_number", "hc2_number", "hc3_number", "name", "date_of_birth"];
  }

  private _generateBenefitRecipientArgs(): string {
    return this._formatFields(this._benefitRecipientFields, this.new_pr_form.patient_exemption || {});
  }

  private _generateProxySignerArgs(): string {
    return this._formatFields(Object.keys(this.new_pr_form.proxy_signer || {}), this.new_pr_form.proxy_signer || {});
  }

  private _getGraphQlMutation(): string {
    const benefitRecipientArgs = this._generateBenefitRecipientArgs();
    const proxySignerArgs = this._generateProxySignerArgs();

    const patientExemption = Object.entries(this.new_pr_form.patient_exemption || {})
      .filter(([key, value]) => !this._benefitRecipientFields.includes(key) && value)
      .filter(([key, _value]) => key !== "nhs_number")
      .map(([key, value]) => `${key}: "${value}"`);

    if (benefitRecipientArgs) {
      patientExemption.push(`benefit_recipient: {${benefitRecipientArgs}}`);
    }

    return `{
      createPatientNhsPrExemption(new_item: {
        ${this.langSwitch().language_code ? `language_code: "${this.langSwitch().language_code}",` : ""}
        ${this.new_pr_form.practitioner_registration_id ? `additional_fields: { value: "${this.new_pr_form.practitioner_registration_id}" }` : ""}
        signature_used: ${this.new_pr_form.signature_used},
        patient_exemption: {
          ${patientExemption.join(",\n")}
        },
        ${proxySignerArgs ? `proxy_signer: {${proxySignerArgs}},` : ""}
        encoded_signature: "${this.new_pr_form.encoded_signature}"
        ${this.new_pr_form.patient_exemption?.nhs_number ? `nhs_number: "${this.new_pr_form.patient_exemption?.nhs_number}"` : ""}
      }) {
        error
        url
      }
    }`;
  }

  private _backToForm(): void {
    this._navigationService.navigate("/my-dental/nhs-pr");
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  public generateDefaultRoute(form_type: E_ActionType | undefined, exemption_code: string | undefined): string {
    if (form_type === E_ActionType.GP17Part2 && exemption_code && !this._canPatientChangeExemption(form_type, exemption_code)) {
      return "/my-dental/nhs-pr/terms";
    }
    return "/my-dental/nhs-pr/form";
  }

  private _canPatientChangeExemption(form_type: E_ActionType, exemption_code: string): boolean {
    const patient_not_fully_exempt_codes = ["0", "H3"];
    return form_type === E_ActionType.GP17Part2 && patient_not_fully_exempt_codes.includes(exemption_code) && this.isScotland;
  }
}
